import {
  createAppStatusIncident,
  deleteAppStatusIncident,
  findAppStatusIncidents,
  updateAppStatusIncident,
} from '@src/main/modules/app_status/application/appStatusIncident.usecases';
import {
  AppStatusIncidentCriteria,
  AppStatusIncidentInput,
} from '@src/main/modules/app_status/domain/appStatusIncident.model';
import { createAppStatusIncidentHttpRepository } from '@src/main/modules/app_status/infrastructure/AppStatusIncidentHttpRepository';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTablePageEvent, DataTableSortEvent } from 'primereact/datatable';
import { useCallback, useMemo, useState } from 'react';

export const APP_STATUS_QUERY_KEY = 'app-status-incidents';

export const useAppStatusIncidents = (criteria: AppStatusIncidentCriteria) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState('severity');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const getToken = useCallback(async () => user?.token ?? '', [user?.token]);
  const tokenProvider = useMemo(() => ({ getToken }), [getToken]);
  const repository = useMemo(
    () => (user?.token ? createAppStatusIncidentHttpRepository(tokenProvider) : null),
    [tokenProvider, user?.token],
  );

  const search = useMemo(
    () => ({
      ...criteria,
      limit: pageSize,
      offset: pageNumber * pageSize,
      sortField,
      sortOrder,
    }),
    [criteria, pageNumber, pageSize, sortField, sortOrder],
  );

  const query = useQuery({
    queryKey: [APP_STATUS_QUERY_KEY, search],
    queryFn: () => {
      if (!repository) throw new Error('Repository not available');
      return findAppStatusIncidents(repository, search);
    },
    enabled: !!repository,
    placeholderData: (previous) => previous,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [APP_STATUS_QUERY_KEY] });

  const createMutation = useMutation({
    mutationFn: (input: AppStatusIncidentInput) => {
      if (!repository) throw new Error('Repository not available');
      return createAppStatusIncident(repository, input);
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: AppStatusIncidentInput }) => {
      if (!repository) throw new Error('Repository not available');
      return updateAppStatusIncident(repository, id, input);
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!repository) throw new Error('Repository not available');
      return deleteAppStatusIncident(repository, id);
    },
    onSuccess: invalidate,
  });

  const handlePage = useCallback((event: DataTablePageEvent) => {
    setPageNumber(Math.floor(event.first / event.rows));
    setPageSize(event.rows);
  }, []);

  const handleSort = useCallback((event: DataTableSortEvent) => {
    setSortField(event.sortField ?? 'severity');
    setSortOrder(event.sortOrder === 1 ? 'asc' : 'desc');
    setPageNumber(0);
  }, []);

  const resetPage = useCallback(() => setPageNumber(0), []);

  return {
    createIncident: createMutation.mutateAsync,
    deleteIncident: deleteMutation.mutateAsync,
    handlePage,
    handleSort,
    incidents: query.data?.items ?? [],
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isSaving: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    pageNumber,
    pageSize,
    refetch: query.refetch,
    resetPage,
    sortField,
    sortOrder,
    total: query.data?.total ?? 0,
    updateIncident: updateMutation.mutateAsync,
  };
};
